/*
 * Copyright (c) 2023 - 2026. Bolmate B.V.
 */

import './style/reviewAnalytics.scss';

import React, {MouseEventHandler, useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import {AwesomeIcon, ChangeFunction, Loader, SelectItemShape} from '@dvrd/dvr-controls';

import IDate from '@dvrd/idate';

import {BolmateButton, BolmateSelect, BolmateTabs} from '../controls';

import {ReviewChartData, ReviewSalesPeriod, ReviewStatsDisplay, ReviewTab} from '../../util/interfaces';

import classNames from 'classnames';

import ReviewsTableController from './reviewsTable/reviewsTableController';

import ReviewFeedController from './reviewFeed/reviewFeedController';

import RatingExportsController from './export/ratingExportsController';

import ReviewChart from './reviewChart/reviewChart';

import {IconName} from '@fortawesome/fontawesome-svg-core';

import {parseThousandSeparator} from '../../util/utils';

import InfoContainer from '../misc/infoContainer';

import InfoBanner from '../misc/infoBanner';

interface Props {
    /** Render only the simplified card feed (mobile layout with feed access). */
    showMobileFeed: boolean;
    onChangePeriod: ChangeFunction<ReviewSalesPeriod>;
    onChangeTab: (tab: ReviewTab) => void;
    onChangeStatsDisplay: MouseEventHandler;
    onClickStat: (stat: 'all' | 'positive' | 'negative') => MouseEventHandler;
    periodValue: ReviewSalesPeriod;
    tab: ReviewTab;
    fromDate: IDate | null;
    toDate: IDate | null;
    allowedTabs: Array<ReviewTab>;
    chartData: Record<string, ReviewChartData>;
    loadingChart: boolean;
    loadingStats: boolean;
    totalPositive: number;
    totalNegative: number;
    totalPositiveIncrease: number;
    totalNegativeIncrease: number;
    npsScore: number;
    periodNpsScore: number;
    statsDisplay: ReviewStatsDisplay;
}

export default function ReviewAnalytics(props: Props) {
    const {t} = useTranslation();
    const periodItems: SelectItemShape[] = [
        {label: t('reviewAnalytics.period.last7Days', 'Afgelopen 7 dagen'), value: ReviewSalesPeriod.SEVEN_DAYS},
        {label: t('reviewAnalytics.period.last30Days', 'Afgelopen 30 dagen'), value: ReviewSalesPeriod.THIRTY_DAYS},
        {label: t('reviewAnalytics.period.total', 'Gehele periode'), value: ReviewSalesPeriod.TOTAL},
        {label: t('reviewAnalytics.period.custom', 'Aangepast...'), value: ReviewSalesPeriod.CUSTOM}
    ];
    const {
        showMobileFeed,
        onChangePeriod,
        periodValue,
        tab,
        onChangeTab,
        allowedTabs,
        fromDate,
        toDate,
        chartData,
        loadingChart,
        loadingStats,
        totalPositiveIncrease,
        totalNegativeIncrease,
        totalPositive,
        totalNegative,
        onChangeStatsDisplay,
        statsDisplay,
        onClickStat,
        npsScore,
        periodNpsScore
    } = props;
    const totalReviews = useMemo(() => {
        return totalPositive + totalNegative;
    }, [totalPositive, totalNegative]);
    const totalIncrease = useMemo(() => {
        return totalPositiveIncrease + totalNegativeIncrease;
    }, [totalPositiveIncrease, totalNegativeIncrease]);
    const periodLabel = useMemo(() => {
        let days: number = 0;
        if (periodValue === ReviewSalesPeriod.TOTAL) return t('reviewAnalytics.periodLabel.total', 'GEHELE PERIODE');
        if (fromDate && toDate) days = toDate.absDiff(fromDate, 'days');
        else if (periodValue === ReviewSalesPeriod.THIRTY_DAYS) days = 30;
        else if (periodValue === ReviewSalesPeriod.SEVEN_DAYS) days = 7;
        return `${t('reviewAnalytics.periodLabel.last', 'AFGELOPEN')} ${days} ${days === 1 ? t('reviewAnalytics.periodLabel.day', 'dag') : t('reviewAnalytics.periodLabel.days', 'dagen')}`;
    }, [periodValue, fromDate, toDate, t]);
    const statDisplayLabel = useMemo(() => {
        if (statsDisplay === ReviewStatsDisplay.STATS_ONLY)
            return t('reviewAnalytics.statsDisplay.showChart', 'Grafiek tonen');
        return t('reviewAnalytics.statsDisplay.hideChart', 'Grafiek verbergen');
    }, [statsDisplay, t]);

    function renderTop() {
        return (
            <div className='top-container full'>
                <ReviewChart chartData={chartData} loading={loadingChart} />
                {renderStats()}
            </div>
        );
    }

    function renderTopStatsOnly() {
        return <div className='top-container stats-only'>{renderStats()}</div>;
    }

    function renderStats() {
        return (
            <div className='review-stats-container'>
                {renderStat(
                    t('reviewAnalytics.stats.totalReviews', 'TOTAAL REVIEWS'),
                    totalReviews,
                    totalIncrease,
                    'star',
                    'total',
                    onClickStat('all')
                )}
                {renderStat(
                    t('reviewAnalytics.stats.positiveReviews', 'POSITIEVE REVIEWS'),
                    totalPositive,
                    totalPositiveIncrease,
                    'face-smile',
                    'positive',
                    onClickStat('positive')
                )}
                {renderStat(
                    t('reviewAnalytics.stats.negativeReviews', 'NEGATIEVE REVIEWS'),
                    totalNegative,
                    totalNegativeIncrease,
                    'face-frown',
                    'negative',
                    onClickStat('negative')
                )}
                {renderNPS()}
            </div>
        );
    }

    function renderStat(
        title: string,
        total: number,
        increase: number,
        icon: IconName,
        className: string,
        onClick?: MouseEventHandler
    ) {
        return (
            <div className={classNames('review-stat-container', className)}>
                <Loader active={loadingStats} size='2.5rem' />
                <div className='icon-container'>
                    <AwesomeIcon name={icon} className='stat-icon' prefix='fas' />
                </div>
                <label className='stat-title'>{title}</label>
                <label className='stat-total'>{parseThousandSeparator(total)}</label>
                {increase > -1 && (
                    <label className='increase-container'>
                        <span className='increase-label'>+{parseThousandSeparator(increase)}</span> {periodLabel}{' '}
                        {!!onClick && (
                            <span className='click-label' onClick={onClick}>
                                ({t('reviewAnalytics.stats.view', 'bekijken')})
                            </span>
                        )}
                    </label>
                )}
            </div>
        );
    }

    function renderNPS() {
        return (
            <div className='review-stat-container nps'>
                <InfoContainer className='nps-info'>
                    {t(
                        'reviewAnalytics.nps.info',
                        'De NPS (net promoter score) geeft aan hoe waarschijnlijk jouw klanten het product dat ze kochten aan zouden raden aan anderen. Deze score wordt berekend door het percentage positieve reviews (5 sterren) te verminderen met het percentage negatieve reviews (1, 2 of 3 sterren). Deze score loopt van -100 t/m 100, waarbij -100 aangeeft dat er alleen maar negatieve reviews zijn en 100 aangeeft dat er alleen maar positieve reviews zijn.'
                    )}
                </InfoContainer>
                <Loader active={loadingStats} size='2.5rem' />
                <div className='icon-container'>
                    <AwesomeIcon name='people-arrows' className='stat-icon' prefix='fas' />
                </div>
                <label className='stat-title'>{t('reviewAnalytics.nps.title', 'Net Promoter Score')}</label>
                <label className='stat-total'>{npsScore}</label>
                <label className='increase-container'>
                    <span className='increase-label'>{periodNpsScore}</span> {periodLabel}
                </label>
            </div>
        );
    }

    const reviewTabLabels: Record<ReviewTab, string> = {
        [ReviewTab.PRODUCTS]: t('reviewAnalytics.tabs.products', 'Producten'),
        [ReviewTab.FEED]: t('reviewAnalytics.tabs.feed', 'Feed'),
        [ReviewTab.EXPORT]: t('reviewAnalytics.tabs.export', 'Exporteren'),
        [ReviewTab.SETTINGS]: t('reviewAnalytics.tabs.settings', 'Instellingen')
    };

    function renderTabs() {
        return (
            <div className='tabs-container'>
                <BolmateTabs<ReviewTab>
                    tabs={allowedTabs.map(tab => ({key: tab, label: reviewTabLabels[tab]}))}
                    activeTab={tab}
                    onChangeTab={onChangeTab}
                />
                <div className='period-container'>
                    <BolmateSelect
                        value={periodValue}
                        onChange={onChangePeriod}
                        items={periodItems}
                        itemsHeight='30rem'
                        highlightSelected
                        itemContainerClassName='period-items-container'
                        className='reviews-period-select'
                    />
                    <BolmateButton
                        onClick={onChangeStatsDisplay}
                        label={statDisplayLabel}
                        disabled={loadingStats}
                        className='btn-stat-display'
                        secondary
                    />
                </div>
            </div>
        );
    }

    function renderTable() {
        return <ReviewsTableController period={periodValue} fromDate={fromDate} toDate={toDate} />;
    }

    // The simplified mobile layout renders only the date-grouped card feed —
    // no banner, chart, stat cards, tabs or period controls. Branches keep the
    // SAME root element and child positions, so crossing the breakpoint (e.g.
    // a tablet rotation) reconciles instead of remounting ReviewFeedController
    // — its loaded pages and star filter survive the layout switch.
    return (
        <>
            {!showMobileFeed && (
                <InfoBanner
                    featureKey='review_insights'
                    variant='info'
                    title={t('reviewAnalytics.banner.title', 'Review inzichten')}
                    showForNewUsersOnly={true}
                >
                    {t(
                        'reviewAnalytics.banner.body',
                        'Bekijk in één overzicht alle reviews op jouw producten. Ontdek trends met BolmateAI.'
                    )}
                </InfoBanner>
            )}
            <div className={classNames('review-analytics', showMobileFeed && 'mobile-simple')}>
                {!showMobileFeed && (statsDisplay === ReviewStatsDisplay.FULL ? renderTop() : renderTopStatsOnly())}
                {!showMobileFeed && renderTabs()}
                {!showMobileFeed && tab === ReviewTab.PRODUCTS && renderTable()}
                {(showMobileFeed || tab === ReviewTab.FEED) && <ReviewFeedController />}
                {!showMobileFeed && tab === ReviewTab.EXPORT && <RatingExportsController />}
            </div>
        </>
    );
}
